import { valueRender } from '@repo/toolkit/conditional-render'
import { useNavigator } from '@repo/toolkit/use-navigator'
import { Column } from '@repo/ui/data-table'
import { TableDate } from '@repo/ui/data-table-cells'
import { BoxIcon, EuroIcon, PlusIcon, UploadIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import React, { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { updateJourneyBasePrice } from '../../api/products/update-journey'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import { ProductDatePriceForm } from '../../components/pages/products/product-date-price-form'
import { ProductsFilter } from '../../components/pages/products/products-filter'
import { Paths } from '../../lib/constants/path'
import { useDownloadDocuments } from '../../lib/hooks/use-download-documents'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import useTranslate from '../../lib/hooks/use-translate'
import { schoolLevelEnumMapping } from '../../lib/types/enums/common/school-level.enum'
import { productTypeEnumMapping } from '../../lib/types/enums/product/product-type.enum'
import { Product, ProductFilters } from '../../lib/types/models/product/product'
import { modalButtonConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformProductFiltersToUrl,
  transformUrlToProductFilters
} from '../../lib/utils/filters-mapping/product'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'

const productInitialFilters: ProductFilters = {}

export function ProductListPage() {
  const { t } = useTranslate()
  const tableRef = React.useRef<DynamicTableRef<Product>>(null)
  const { navigate, navigateWithEvent } = useNavigator()
  const { downloadDocuments } = useDownloadDocuments()
  const [isTableLoading, setIsTableLoading] = useState(false)

  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'filters' | 'editPrice',
    Product[]
  >(['filters', 'editPrice'])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<ProductFilters>({
      initialFilters: productInitialFilters,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformProductFiltersToUrl,
      transformUrlToFilter: transformUrlToProductFilters
    })

  const { mutateAsync: updateJourneyBasePriceMutation } = useMutation({
    mutationFn: updateJourneyBasePrice,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Prix de base modifié avec succès')
      tableRef.current?.refetch()
      closeModel('editPrice')
    }
  })

  const columns: Column<Product>[] = [
    {
      accessorKey: 'name',
      header: 'NOM',
      enableSorting: true
    },
    {
      accessorKey: 'type',
      header: 'Type',
      enableSorting: true,
      cell: ({ row }) => t(productTypeEnumMapping[row.original.type].title)
    },
    {
      accessorKey: 'minAge',
      header: 'Âge min',
      enableSorting: true,
      cell: ({ row }) => row.original.minAge ?? '-'
    },
    {
      accessorKey: 'maxAge',
      header: 'Âge max',
      enableSorting: true,
      cell: ({ row }) => row.original.maxAge ?? '-'
    },
    {
      accessorKey: 'minSchoolLevel',
      header: 'Classe min',
      enableSorting: true,
      cell: ({ row }) => schoolLevelEnumMapping[row.original.minSchoolLevel]
    },
    {
      accessorKey: 'maxSchoolLevel',
      header: 'Classe max',
      enableSorting: true,
      cell: ({ row }) => valueRender(row.original.maxSchoolLevel, (v) => schoolLevelEnumMapping[v])
    },
    {
      accessorKey: 'minNumberOfStudents',
      header: "Nb d'élèves min",
      enableSorting: true,
      cell: ({ row }) => row.original.minNumberOfStudents ?? '-'
    },
    {
      accessorKey: 'maxNumberOfStudents',
      header: "Nb d'élèves max",
      enableSorting: true,
      cell: ({ row }) => row.original.maxNumberOfStudents ?? '-'
    },
    {
      accessorKey: 'numberOfHoursPerDay',
      header: "Nb d'h de cours / j",
      enableSorting: true,
      cell: ({ row }) => row.original.numberOfHoursPerDay ?? '-'
    },
    {
      accessorKey: 'numberOfHoursPerWeek',
      header: "Nb d'h de cours / sem",
      enableSorting: true,
      cell: ({ row }) => row.original.numberOfHoursPerWeek ?? '-'
    },
    {
      accessorKey: 'updatedAt',
      header: 'Mise à jour',
      enableSorting: true,
      cell: ({ row }) => <TableDate date={row.original.updatedAt} />
    }
  ]

  const journeyDateIds = useMemo(() => {
    if (!selectedData) return []
    const dates = selectedData.map((product) => product.dates).flat()
    return dates.filter((date, index) => dates.indexOf(date) === index).map((date) => date.id)
  }, [selectedData])

  return (
    <div>
      <UserNavBar title={'Produits'} icon={BoxIcon} />
      <div className={'h-4'} />

      <DynamicDataTable<Product, ProductFilters>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        title={'Tous les produits'}
        deleteMessage={{
          name: 'produits',
          attributeName: 'name',
          deleteOne: (product) =>
            `Êtes-vous sûr de vouloir supprimer ce produit "${product.name}"?`
        }}
        columns={columns}
        allowSearchParam
        selectable
        endpoint={API.products.getAll.url}
        onRowClick={(row, event) =>
          navigateWithEvent(Paths.PRODUCT_UPDATE_PAGE.replace(':id', row.id.toString()), event)
        }
        filters={selectedFilter}
        onFiltersClick={() => toggleModal('filters')}
        filtersLength={countFilters(filter)}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            variant: 'primary',
            label: 'Ajouter un nouveau produit',
            fn: () => navigate(Paths.PRODUCT_CREATE_PAGE)
          },
          {
            type: 'multiple',
            icon: UploadIcon,
            variant: 'ghost',
            label: 'Exporter',
            fn: (products) => {
              const ids = products.map((product) => product.id)
              downloadDocuments({
                endpoint: API.products.exportExcel.url,
                ids
              })
            }
          },
          {
            type: 'multiple',
            icon: EuroIcon,
            variant: 'ghost',
            label: 'Modifier les prix de base',
            fn: (products) => toggleModal('editPrice', products)
          }
        ]}
      />

      {selectedData && (
        <Modal
          isOpen={modalState.editPrice}
          title={'Modifier les prix de base'}
          icon={EuroIcon}
          hideActions
          onCancel={() => closeModel('editPrice')}
        >
          <ProductDatePriceForm
            className={MODAL_BODY_CLASSNAME}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('editPrice'),
              onConfirm: (values) =>
                updateJourneyBasePriceMutation({ ...values, ids: journeyDateIds })
            })}
          />
        </Modal>
      )}

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <ProductsFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}
